import { inject } from '@angular/core';
import { ApiQuery } from '@kinet-open-network/client/core/api-client';
import {
  callApiMethod,
  setError,
  setLoaded,
  setLoading,
  withExport,
  withResourceState,
} from '@kinet-open-network/client/shared/data-access/state';
import {
  IPaymentActionData,
  IPaymentBatchActionData,
  ITransferPaymentData,
} from '@kinet-open-network/shared/data-access/model';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { Payment } from '../models/payment.model';
import { LiquidationsService } from '../services/liquidations.service';
import { PaymentsService } from '../services/payments.service';

export const PaymentsStore = signalStore(
  { protectedState: false },
  withResourceState(PaymentsService, {
    query: {
      initial: {
        sort: ['-date'],
      },
    },
  }),
  withExport(PaymentsService),
  withMethods(
    (
      store,
      paymentService = inject(PaymentsService),
      liquidationService = inject(LiquidationsService),
    ) => ({
      pairPayment: async (paymentId: string) => {
        patchState(store, setLoading());
        return callApiMethod(() => paymentService.pair(paymentId), {
          next: (payment) => {
            patchState(store, setLoaded());
          },
          error: () => {
            patchState(store, setLoaded());
          },
        });
      },
      transferPayment: async (
        paymentId: string,
        data: ITransferPaymentData,
      ) => {
        patchState(store, setLoading());
        try {
          const result = await firstValueFrom(
            paymentService.transfer(paymentId, data),
          );
          if (store.model()?.id === result.payment.id) {
            patchState(store, { model: result.payment });
          }
          patchState(store, setLoaded());
          return result;
        } catch (error) {
          patchState(store, setError(error));
          throw error;
        }
      },
      removeLiquidation: async (liquidationId: string) => {
        patchState(store, setLoading());

        try {
          await firstValueFrom(liquidationService.delete(liquidationId));

          const payment = store.model();
          const liquidations =
            payment?.liquidations?.filter((l) => l.id !== liquidationId) ?? [];

          if (payment?.id && liquidations) {
            patchState(store, setLoaded(), {
              model: new Payment({
                ...payment,
                liquidations,
              }),
            });
          }
        } finally {
          patchState(store, setLoaded());
        }
      },
      runAction: (data: IPaymentActionData) => {
        patchState(store, setLoading());

        return callApiMethod(() => paymentService.runAction(data), {
          next: () => patchState(store, setLoaded()),
          error: (err) => patchState(store, setError(err)),
        });
      },
      batchAction: (data: IPaymentBatchActionData, query?: ApiQuery | null) => {
        patchState(store, setLoading());

        return callApiMethod(() => paymentService.batchAction(data, query), {
          next: () => patchState(store, setLoaded()),
          error: (err) => patchState(store, setError(err)),
        });
      },
    }),
  ),
);
